import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { Modal } from '../components/Modal.jsx';
import { FormIcon } from '../forms/FormIcon.jsx';
import { FormMenu } from '../forms/FormMenu.jsx';
import { FormRowIcon, StatusPill, TypePill } from '../forms/FormBadges.jsx';
import { CATEGORIES, SORTS, lastResponseLabel } from '../forms/formSchema.js';
import '../forms/forms.css';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

const EMPTY_STATS = { total_forms: 0, total_submissions: 0, new_submissions: 0, awaiting_response: 0, past_due: 0 };
const STATUS_FILTER_LABELS = { waiting: 'Awaiting response', overdue: 'Past due', draft: 'Drafts', active: 'Active', closed: 'Closed' };

function StatCard({ icon, tone, value, label, sub, onClick, active }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} className={`fm-stat${onClick ? ' is-clickable' : ''}${active ? ' is-active' : ''}`} onClick={onClick}>
      <span className={`fm-stat-icon fm-stat-icon--${tone}`}>
        <FormIcon name={icon} size={22} />
      </span>
      <span className="fm-stat-text">
        <strong>{value}</strong>
        <span>{label}</span>
        {sub ? <em>{sub}</em> : null}
      </span>
    </Tag>
  );
}

function submissionsLabel(form) {
  if (form.category === 'template') return form.submissions_count || 0;
  const done = form.completed_submissions_count ?? form.submissions_count;
  if (form.expected_responses) return `${Math.min(done, form.expected_responses)} / ${form.expected_responses}`;
  if (form.partial_submissions_count && !done) return 'Partial';
  return form.submissions_count;
}

function pageList(current, last) {
  const pages = new Set([1, last, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= last).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
}

export function AdminFormsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || 'all';
  const sort = searchParams.get('sort') || 'updated';
  const status = searchParams.get('status') || 'all';
  const page = Number(searchParams.get('page') || 1);
  const urlQuery = searchParams.get('q') || '';

  const [search, setSearch] = useState(urlQuery);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 10 });
  const [stats, setStats] = useState(EMPTY_STATS);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [options, setOptions] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => setSearch(urlQuery), [urlQuery]);

  const setParams = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([key, value]) => {
        const isDefault = !value || value === 'all' || (key === 'sort' && value === 'updated') || (key === 'page' && Number(value) === 1);
        if (isDefault) next.delete(key);
        else next.set(key, String(value));
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== urlQuery) setParams({ q: search.trim(), page: 1 });
    }, 300);
    return () => clearTimeout(t);
  }, [search, urlQuery, setParams]);

  const load = useCallback(() => {
    const qs = new URLSearchParams({ category, sort, status, page: String(page) });
    if (urlQuery) qs.set('q', urlQuery);
    setLoading(true);
    return apiJson(`/api/admin/forms?${qs}`)
      .then((data) => {
        setRows(data.data || []);
        setMeta(data.meta || {});
        setStats(data.stats || EMPTY_STATS);
        setCounts(data.counts || {});
        setError('');
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [category, sort, status, page, urlQuery]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadOptions = useCallback(() => {
    if (options) return Promise.resolve(options);
    return apiJson('/api/admin/forms/options').then((data) => {
      setOptions(data);
      return data;
    });
  }, [options]);

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(''), 3500);
  };

  const duplicate = async (form, as = 'copy') => {
    try {
      const data = await apiJson(`/api/admin/forms/${form.id}/duplicate`, { method: 'POST', body: { as } });
      if (as === 'from_template') {
        navigate(`/admin/forms/${data.form.id}`);
        return;
      }
      flash(as === 'template' ? `Saved “${form.title}” as a template.` : `Duplicated “${form.title}”.`);
      setOptions(null);
      void load();
    } catch (e) {
      setError(e.message);
    }
  };

  const copyLink = async (form) => {
    const base = options?.frontend_url || window.location.origin;
    try {
      await navigator.clipboard.writeText(`${base}/f/${form.public_key}`);
      flash('Public link copied.');
    } catch {
      setError('Could not copy the link.');
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await apiJson(`/api/admin/forms/${deleting.id}`, { method: 'DELETE' });
      flash(`Deleted “${deleting.title}”.`);
      setDeleting(null);
      setOptions(null);
      void load();
    } catch (e) {
      setError(e.message);
      setDeleting(null);
    }
  };

  const from = meta.total ? (meta.current_page - 1) * meta.per_page + 1 : 0;
  const to = Math.min(meta.total || 0, (meta.current_page || 1) * (meta.per_page || 10));
  const pages = useMemo(() => pageList(meta.current_page || 1, meta.last_page || 1), [meta]);

  return (
    <div className="fm-page">
      <header className="fm-page-head">
        <div>
          <h1>Forms</h1>
          <p>Create forms, questionnaires and information requests for your clients and websites.</p>
        </div>
        <button
          type="button"
          className="fm-btn fm-btn--blue fm-btn--lg"
          onClick={() => {
            setCreateOpen(true);
            loadOptions().catch((e) => setError(e.message));
          }}
          data-tour="forms-create"
        >
          <FormIcon name="plus" size={18} strokeWidth={2.2} /> Create Form
        </button>
      </header>

      <div className="fm-stats">
        <StatCard icon="forms" tone="purple" value={stats.total_forms} label="Total Forms" onClick={() => setParams({ status: 'all', category: 'all', page: 1 })} />
        <StatCard
          icon="submissions"
          tone="green"
          value={stats.total_submissions}
          label="Total Submissions"
          sub={stats.new_submissions ? <span className="fm-stat-sub--blue">{stats.new_submissions} new</span> : null}
        />
        <StatCard
          icon="clock"
          tone="amber"
          value={stats.awaiting_response}
          label="Awaiting Response"
          active={status === 'waiting'}
          onClick={() => setParams({ status: status === 'waiting' ? 'all' : 'waiting', page: 1 })}
        />
        <StatCard
          icon="alert"
          tone="red"
          value={stats.past_due}
          label="Client Requests"
          sub="Past Due"
          active={status === 'overdue'}
          onClick={() => setParams({ status: status === 'overdue' ? 'all' : 'overdue', page: 1 })}
        />
      </div>

      <div className="fm-toolbar" data-tour="forms-list">
        <div className="fm-chips" role="tablist" aria-label="Form type">
          {[{ id: 'all', plural: 'All' }, ...CATEGORIES].map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={category === c.id}
              className={`fm-chip${category === c.id ? ' is-on' : ''}`}
              onClick={() => setParams({ category: c.id, page: 1 })}
            >
              {c.plural}
              {counts[c.id] ? <span className="fm-chip-count">{counts[c.id]}</span> : null}
            </button>
          ))}
          {status !== 'all' ? (
            <button type="button" className="fm-chip fm-chip--filter" onClick={() => setParams({ status: 'all', page: 1 })}>
              {STATUS_FILTER_LABELS[status] || status}
              <FormIcon name="x" size={13} />
            </button>
          ) : null}
        </div>
        <div className="fm-toolbar-right">
          <label className="fm-search">
            <FormIcon name="search" size={16} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search forms..." aria-label="Search forms" />
          </label>
          <label className="fm-sort">
            <span>Sort:</span>
            <SearchableSelect value={sort} onChange={(e) => setParams({ sort: e.target.value, page: 1 })} aria-label="Sort forms">
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </SearchableSelect>
            <FormIcon name="chevronDown" size={15} />
          </label>
        </div>
      </div>

      {error ? <p className="fm-alert fm-alert--error">{error}</p> : null}
      {notice ? <p className="fm-alert fm-alert--ok">{notice}</p> : null}

      <div className="fm-card fm-table-card">
        <table className="fm-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Used On / Assigned To</th>
              <th>Submissions</th>
              <th>Last Response</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((form) => (
              <tr key={form.id} onClick={() => navigate(`/admin/forms/${form.id}`)} className="is-link">
                <td>
                  <div className="fm-name-cell">
                    <FormRowIcon category={form.category} />
                    <Link to={`/admin/forms/${form.id}`} onClick={(e) => e.stopPropagation()}>
                      {form.title}
                    </Link>
                    {form.new_submissions_count ? <span className="fm-new-dot" title={`${form.new_submissions_count} new`}>{form.new_submissions_count}</span> : null}
                  </div>
                </td>
                <td>
                  <TypePill category={form.category} />
                </td>
                <td className="fm-muted-cell">
                  {form.assigned_label}
                  {form.due_date && form.expected_responses ? (
                    <div className={`fm-due${form.is_past_due ? ' is-late' : ''}`}>Due {new Date(`${form.due_date}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                  ) : null}
                </td>
                <td>{submissionsLabel(form)}</td>
                <td className="fm-muted-cell">{lastResponseLabel(form.last_response_at)}</td>
                <td>
                  <StatusPill status={form.display_status} />
                </td>
                <td className="fm-actions-cell" onClick={(e) => e.stopPropagation()}>
                  <FormMenu
                    label={`Actions for ${form.title}`}
                    items={[
                      { label: 'Edit', icon: 'edit', onClick: () => navigate(`/admin/forms/${form.id}`) },
                      form.category !== 'template' && { label: 'View submissions', icon: 'inbox', onClick: () => navigate(`/admin/forms/${form.id}/submissions`) },
                      form.category === 'template'
                        ? { label: 'Use template', icon: 'plus', onClick: () => duplicate(form, 'from_template') }
                        : { label: 'Duplicate', icon: 'copy', onClick: () => duplicate(form) },
                      form.category !== 'template' && { label: 'Save as template', icon: 'template', onClick: () => duplicate(form, 'template') },
                      form.category === 'template' && { label: 'Duplicate', icon: 'copy', onClick: () => duplicate(form) },
                      form.audience !== 'internal' && form.category !== 'template' && form.status !== 'draft' && {
                        label: 'Copy public link',
                        icon: 'url',
                        onClick: () => loadOptions().then(() => copyLink(form)),
                      },
                      { label: 'Delete', icon: 'trash', danger: true, onClick: () => setDeleting(form) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && !rows.length ? <p className="fm-empty">Loading forms…</p> : null}
        {!loading && !rows.length ? (
          <div className="fm-empty">
            <FormIcon name="forms" size={28} />
            <p>{urlQuery || category !== 'all' || status !== 'all' ? 'No forms match this view.' : 'No forms yet. Create your first form to start collecting answers.'}</p>
          </div>
        ) : null}
        {meta.total > 0 ? (
          <footer className="fm-pager">
            <span>
              Showing {from}–{to} of {meta.total}
            </span>
            <div className="fm-pager-btns">
              <button type="button" className="fm-icon-btn" disabled={meta.current_page <= 1} onClick={() => setParams({ page: meta.current_page - 1 })} aria-label="Previous page">
                <FormIcon name="chevronLeft" size={16} />
              </button>
              {pages.map((p, i) =>
                p === '…' ? (
                  <span key={`gap-${i}`} className="fm-pager-gap">
                    …
                  </span>
                ) : (
                  <button key={p} type="button" className={`fm-pager-num${p === meta.current_page ? ' is-on' : ''}`} onClick={() => setParams({ page: p })}>
                    {p}
                  </button>
                ),
              )}
              <button
                type="button"
                className="fm-icon-btn"
                disabled={meta.current_page >= meta.last_page}
                onClick={() => setParams({ page: meta.current_page + 1 })}
                aria-label="Next page"
              >
                <FormIcon name="chevronRight" size={16} />
              </button>
            </div>
          </footer>
        ) : null}
      </div>

      <CreateFormModal
        open={createOpen}
        options={options}
        defaultCategory={category !== 'all' ? category : 'questionnaire'}
        onClose={() => setCreateOpen(false)}
        onCreated={(form) => navigate(`/admin/forms/${form.id}`)}
      />

      <Modal
        open={Boolean(deleting)}
        title="Delete form?"
        description={deleting ? `“${deleting.title}” and all ${deleting.submissions_count} submission(s) and uploaded files will be permanently removed.` : ''}
        onClose={() => setDeleting(null)}
        footer={
          <>
            <button type="button" className="fm-btn" onClick={() => setDeleting(null)}>
              Cancel
            </button>
            <button type="button" className="fm-btn fm-btn--danger-solid" onClick={confirmDelete}>
              Delete form
            </button>
          </>
        }
      >
        <p className="muted">This cannot be undone.</p>
      </Modal>
    </div>
  );
}

function CreateFormModal({ open, options, defaultCategory, onClose, onCreated }) {
  const [draft, setDraft] = useState({ title: '', category: defaultCategory, client_id: '', project_id: '', due_date: '', template_id: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setDraft({ title: '', category: defaultCategory, client_id: '', project_id: '', due_date: '', template_id: '' });
      setError('');
      setBusy(false);
    }
  }, [open, defaultCategory]);

  const projects = (options?.projects || []).filter((p) => !draft.client_id || String(p.client_id) === String(draft.client_id));
  const isClientForm = draft.category === 'questionnaire' || draft.category === 'client_request';
  const set = (patch) => setDraft((cur) => ({ ...cur, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const data = await apiJson('/api/admin/forms', {
        method: 'POST',
        body: {
          title: draft.title.trim(),
          category: draft.category,
          client_id: isClientForm && draft.client_id ? Number(draft.client_id) : null,
          project_id: isClientForm && draft.project_id ? Number(draft.project_id) : null,
          due_date: isClientForm && draft.due_date ? draft.due_date : null,
          template_id: draft.template_id ? Number(draft.template_id) : null,
        },
      });
      onCreated(data.form);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Create form"
      description="Start blank or from a template. You can change everything in the builder."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="fm-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="fm-create" className="fm-btn fm-btn--blue" disabled={busy || !draft.title.trim()}>
            {busy ? 'Creating…' : 'Continue to builder'}
          </button>
        </>
      }
    >
      <form id="fm-create" className="fm-stack" onSubmit={submit}>
        <label className="fm-field">
          <span>Form name</span>
          <input value={draft.title} onChange={(e) => set({ title: e.target.value })} placeholder="Website Discovery Questionnaire" autoFocus required />
        </label>
        <div className="fm-type-grid">
          {CATEGORIES.map((c) => (
            <label key={c.id} className={`fm-type-option${draft.category === c.id ? ' is-on' : ''}`}>
              <input type="radio" name="fm-category" checked={draft.category === c.id} onChange={() => set({ category: c.id })} />
              <TypePill category={c.id} />
            </label>
          ))}
        </div>
        {options?.templates?.length && draft.category !== 'template' ? (
          <label className="fm-field">
            <span>Start from template (optional)</span>
            <SearchableSelect value={draft.template_id} onChange={(e) => set({ template_id: e.target.value })}>
              <option value="">Blank form</option>
              {options.templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </SearchableSelect>
          </label>
        ) : null}
        {isClientForm ? (
          <>
            <div className="fm-grid-2">
              <label className="fm-field">
                <span>
                  Assign to client <em>(optional)</em>
                </span>
                <SearchableSelect value={draft.client_id} onChange={(e) => set({ client_id: e.target.value, project_id: '' })}>
                  <option value="">No client yet</option>
                  {(options?.clients || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </SearchableSelect>
              </label>
              <label className="fm-field">
                <span>
                  Assign to project <em>(optional)</em>
                </span>
                <SearchableSelect value={draft.project_id} onChange={(e) => set({ project_id: e.target.value })} disabled={!projects.length}>
                  <option value="">{projects.length ? 'No project' : 'No projects'}</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </SearchableSelect>
              </label>
            </div>
            <label className="fm-field">
              <span>
                Due date <em>(optional)</em>
              </span>
              <input type="date" value={draft.due_date} onChange={(e) => set({ due_date: e.target.value })} />
            </label>
          </>
        ) : null}
        {error ? <p className="fm-alert fm-alert--error">{error}</p> : null}
      </form>
    </Modal>
  );
}
